<template>
  <ui-modal-layout :title="tr('Invite a teammember')" :icon="UserPlusIcon">
    <form
      id="invite-member-form"
      class="flex flex-col gap-4"
      novalidate
      @submit.prevent="() => submitForm()"
    >
      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Access level') }}</ui-label>
        <ui-select v-model="level">
          <ui-select-trigger class="w-full" :aria-label="tr('Access level')">
            <ui-select-value />
          </ui-select-trigger>
          <ui-select-content>
            <ui-select-item v-for="item in userLevels" :key="item.value" :value="item.value">
              {{ item.label }}
            </ui-select-item>
          </ui-select-content>
        </ui-select>
      </div>

      <div class="grid gap-4 sm:grid-cols-2">
        <div v-for="field in nameFields" :key="field.key" class="flex flex-col gap-1.5">
          <ui-label :for="'invite-' + field.key">{{ field.label }}</ui-label>
          <ui-input :id="'invite-' + field.key" v-model="$data[field.key]" autocomplete="off" />
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label for="invite-email">{{ tr('E-mail') }}</ui-label>
        <ui-input
          id="invite-email"
          v-model="email"
          type="email"
          autocomplete="off"
        />
      </div>

      <div v-if="level === 'restricted_viewer' || level === 'restricted_editor'" class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Visible Brands') }}</ui-label>
        <multi-select
          v-model="visibleBrandIds"
          :options="brands"
          :append-to-body="false"
          value-key="id"
          label-key="name"
        />
      </div>
    </form>

    <template #footer>
      <ui-button variant="outline" :disabled="isLoading" @click="() => close()">
        {{ tr('Cancel') }}
      </ui-button>
      <ui-button type="submit" form="invite-member-form" :disabled="isLoading">
        <loader-circle-icon v-if="isLoading" class="animate-spin" />
        <send-icon v-else />
        {{ tr('Invite') }}
      </ui-button>
    </template>
  </ui-modal-layout>
</template>

<script>
import { LoaderCircle, Send, UserPlus } from '@lucide/vue';
import { markRaw } from 'vue';

import MultiSelect from '@/ui/components/MultiSelect.vue';
import { isValidEmail } from '@/utils/validators';

export default {
  name: 'InviteMemberModal',
  components: {
    LoaderCircleIcon: LoaderCircle,
    MultiSelect,
    SendIcon: Send,
  },
  data:() => ({
    UserPlusIcon: markRaw(UserPlus),
    email: '',
    firstname: '',
    lastname: '',
    level: 'normal',
    isLoading: false,
    visibleBrandIds: [],
  }),
  computed: {
    // Hungarian names start with the last name.
    nameFields() {
      const fields = [
        { key: 'firstname', label: this.tr('First name') },
        { key: 'lastname', label: this.tr('Last name') },
      ];

      return this.$root.language === 'hu' ? fields.reverse() : fields;
    },
    brands() {
      return this.$root.currentCompany.brands || [];
    },
    userLevels() {
      const levels = [
        { label: this.tr('Admin'), value: 'admin' },
        { label: this.tr('Normal'), value: 'normal' },
        { label: this.tr('Restricted Editor'), value: 'restricted_editor' },
        { label: this.tr('Restricted Viewer'), value: 'restricted_viewer' },
      ];
      if (this.$root.loggedInUser?.level === 'owner') {
        levels.unshift({ label: this.tr('Owner'), value: 'owner' });
      }
      return levels;
    },
  },
  methods: {
    resetForm() {
      this.firstname = '';
      this.lastname = '';
      this.email = '';
      this.level = 'normal';
      this.visibleBrandIds = [];
    },
    close() {
      this.resetForm();
      this.$emit('close');
    },
    submitForm() {
      if (!this.validate()) {
        return;
      }

      const data = {
        firstname: this.firstname.trim(),
        lastname: this.lastname.trim(),
        email: this.email,
        level: this.level,
        visibleBrandIds: this.visibleBrandIds,
      };

      this.isLoading = true;
      this.$store
        .dispatch('invites/createInvitation', data)
        .then(resp => {
          this.$emit('invited', resp);
          this.close();
          this.$toast.success(this.tr('Invitation has been sent successfully.'));

          return resp;
        })
        .catch(err => this.error(err))
        .finally(() => this.isLoading = false);
    },
    validate() {
      this.$toast.clear();

      if (!isValidEmail(this.email)) {
        this.$toast.error(this.tr('E-mail provided has an incorrect format!'));
        return false;
      }
      if (this.firstname.trim().length === 0 || this.lastname.trim().length === 0) {
        this.$toast.error(this.tr('Name field cannot be empty!'));
        return false;
      }
      if ((this.level === 'restricted_viewer' || this.level === 'restricted_editor') && this.visibleBrandIds.length === 0) {
        this.$toast.error(this.tr('Specifying at least one Brand is required.'));
        return false;
      }

      return true;
    },
  },
};
</script>
